// Page docs (structure + permissions + flows): pages/organizations/README.md
import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import Head from "next/head";
import { Box, Container, Typography, Button, Badge, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { useRouter } from "next/router";
import NavBar from "@/components/Importable/Navigation";
import { assetUrl } from "@/utils/asset";
import { useTagPreferences } from "@/hooks/useTagPreferences";
import SectionFooter from "../../components/CreateForm/Footer";
import { getAllNewlyAdded, setDbVersion, isOrgDeactivated, doesRegionLabelMatchSelection, doesRegionMatchPrefs, getSavedFilters, setSavedFilters, setCreatePrefill, clearCreatePrefill, removeRejectedById, getIndexCache, setIndexCache } from "@/utils/localStore";
import { trackMerge } from "@/utils/trafficTracker";
import filterTagData from "@/data/filterTagData.json";
import { searchSite } from "@/utils/siteSearch";
import { orange, gray } from "@/theme/colors";
import { buildOrganizationRouteId } from "@/utils/orgRoutes";
import { useAuth } from "@/context/AuthContext";
import { useSavedItems } from "@/hooks/useSavedItems";
import { useDataVersionCheck } from "@/hooks/useDataVersionCheck";
import { useGetPromotionsQuery } from "@/src/services/api";
import { useGetOrganizationsQuery, useGetMySubmissionsQuery, useGetSubmissionTasksQuery, useDiscardSubmissionMutation } from "@/src/services/organizationsApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAppDispatch } from "@/src/store/hooks";
import { upsertListItems } from "@/src/store/features/organizationsSlice";
import { openAccessGateModal } from "@/utils/accessGate";
import { useProgressiveLoading } from "@/hooks/useProgressiveLoading";
import { FeaturedOrganizationSection } from "@/components/OrganizationSections/FeaturedOrganization";
import OrganizationDirectoryHeader from "@/components/OrganizationSections/OrganizationDirectoryHeader";
import FilterBar from "@/components/OrganizationSections/FilterBar";
import ResultsSection from "@/components/OrganizationSections/ResultsSection";
import { useIndexLayout } from "@/hooks/useIndexLayout";
import IndexPanelSurface, { panelPageSx } from "@/components/Importable/IndexPanelSurface";
import { MAIN_CONTENT_ID } from "@/components/Importable/SkipLink";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const isStaticExport = process.env.NEXT_OUTPUT_MODE === "export";
const canonicalizeIdentifier = value => String(value ?? "").toLowerCase().replace(/[^a-z0-9]+/g, "");
const getOrganizationKey = org => {
  const candidates = [org.backendId, org.id, org.slug];
  for (const candidate of candidates) {
    const key = canonicalizeIdentifier(candidate);
    if (key) return key;
  }
  return "";
};
const dedupeOrganizations = list => {
  const map = new Map();
  list.forEach(org => {
    const key = getOrganizationKey(org);
    if (!key) {
      map.set(`unknown-${map.size}`, org);
      return;
    }
    const existing = map.get(key);
    if (!existing) {
      map.set(key, org);
      return;
    }
    const preferCurrent = Boolean(org.backendId && !existing.backendId);
    if (preferCurrent) {
      map.set(key, org);
    }
  });
  return Array.from(map.values());
};
const getOrgFreshnessTimestamp = org => {
  const timestamps = [];
  const latest = org.latestApprovalTimestamp;
  if (typeof latest === "number" && Number.isFinite(latest)) {
    timestamps.push(latest);
  }
  const approved = org.__approvedTs;
  if (typeof approved === "number" && Number.isFinite(approved)) {
    timestamps.push(approved);
  }
  const updatedAt = org?.updatedAt;
  if (updatedAt) {
    if (typeof updatedAt === "number" && Number.isFinite(updatedAt)) {
      timestamps.push(updatedAt);
    } else if (typeof updatedAt === "string") {
      const parsed = Date.parse(updatedAt);
      if (!Number.isNaN(parsed)) timestamps.push(parsed);
    } else if (updatedAt instanceof Date) {
      const parsed = updatedAt.getTime();
      if (Number.isFinite(parsed)) timestamps.push(parsed);
    }
  }
  if (!timestamps.length) return 0;
  return Math.max(...timestamps);
};

// Styled components
const MainContainer = styled(Box)(() => ({
  display: "flex",
  minHeight: "100vh",
  overflowX: "hidden",
  width: "100%",
  maxWidth: "100vw",
  backgroundColor: alpha(gray.light, 0.1)
}));
const ContentWrapper = styled(Box)(({
  theme
}) => ({
  flexGrow: 1,
  marginLeft: 0,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: "hidden",
  maxWidth: "100%",
  width: "100%"
}));
const StyledContainer = styled(Container)(({
  theme
}) => ({
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4)
}));

/** Stable default: a fresh [] literal per render would recreate the
 *  recompute callback (it depends on `organizations`) every render, and on
 *  cache-less first loads the mount effect then loops setOrgs(new array)
 *  → re-render → new [] → loop, until React trips max update depth. */
const NO_SSR_ORGS = [];
const OrganizationIndexPage = ({
  organizations = NO_SSR_ORGS
}) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const {
    isAuthenticated,
    user,
    token
  } = useAuth();
  const isAdmin = isAuthenticated && user?.role === "admin";
  const cacheStale = useDataVersionCheck("organizations");

  // query, but allow a brief retry window because `getIndexCache` returns
  // null until the module-level `hydrated` flag flips after the initial
  // paint (otherwise we'd race past the cache and trigger an unnecessary
  // network roundtrip on hard refresh).
  const [cachedOrgs, setCachedOrgs] = useState(null);
  // Always start from SSR props so the first render matches server HTML
  // (avoids hydration mismatch when in-memory cache survives HMR).
  const [orgs, setOrgs] = useState(organizations);

  // Tracks whether the page has mounted on the client. We must match the
  // SSR-rendered DOM exactly on the very first client render, so any
  // localStorage-backed reads (deactivated org list, etc.) need to be
  // gated until after this flips true. Otherwise React throws #418 and
  // the JSS class numbering shifts, leaving the footer / other elements
  // styled with sheets meant for siblings.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // Hydrate from index cache after mount (SSR-safe). Try immediately,
  // then retry on the next macrotask in case localStore's hydration timer
  // hadn't fired yet on the first attempt.
  useEffect(() => {
    const apply = () => {
      const c = getIndexCache("organizations");
      if (c?.length) {
        setCachedOrgs(c);
        setOrgs(prev => prev.length === 0 ? c : prev);
        return true;
      }
      return false;
    };
    if (apply()) return;
    const t = setTimeout(apply, 0);
    return () => clearTimeout(t);
  }, []);
  const {
    data: organizationsQuery,
    refetch: refetchOrganizations
  } = useGetOrganizationsQuery(undefined, {
    skip: (cachedOrgs?.length ?? 0) > 0 && !cacheStale
  });
  const {
    data: mySubmissions = []
  } = useGetMySubmissionsQuery(undefined, {
    skip: !token
  });
  const userSubmissions = useMemo(() => {
    if (!user?.id) return [];
    return mySubmissions.filter(submission => submission.submittedByUserId === user.id);
  }, [mySubmissions, user?.id]);
  const {
    pendingSubmissions,
    rejectedSubmissions
  } = useMemo(() => {
    const toTime = value => value ? new Date(value).getTime() : 0;
    const keyFor = submission => {
      const payload = submission?.payload || {};
      const history = Array.isArray(payload.submissionHistory) ? payload.submissionHistory : [];
      return String(history[0] || payload.rootSubmissionId || payload.previousSubmissionId || payload.slug || payload.id || payload.orgName || submission.id);
    };
    const grouped = new Map();
    userSubmissions.forEach(submission => {
      const key = keyFor(submission);
      const existing = grouped.get(key);
      if (!existing) {
        grouped.set(key, submission);
        return;
      }
      const existingTime = toTime(existing.reviewedAt) || toTime(existing.updatedAt) || toTime(existing.createdAt);
      const nextTime = toTime(submission.reviewedAt) || toTime(submission.updatedAt) || toTime(submission.createdAt);
      if (nextTime >= existingTime) grouped.set(key, submission);
    });
    const latest = Array.from(grouped.values());
    return {
      pendingSubmissions: latest.filter(submission => String(submission.status || "").toLowerCase() === "pending"),
      rejectedSubmissions: latest.filter(submission => String(submission.status || "").toLowerCase() === "rejected")
    };
  }, [userSubmissions]);
  const latestPendingSubmission = useMemo(() => {
    if (pendingSubmissions.length === 0) return null;
    const toTime = value => value ? new Date(value).getTime() : 0;
    const sorted = [...pendingSubmissions].sort((a, b) => {
      const aTime = toTime(a.updatedAt) || toTime(a.createdAt) || toTime(a.reviewedAt);
      const bTime = toTime(b.updatedAt) || toTime(b.createdAt) || toTime(b.reviewedAt);
      return bTime - aTime;
    });
    return sorted[0] ?? null;
  }, [pendingSubmissions]);
  const latestRejectedSubmission = useMemo(() => {
    if (rejectedSubmissions.length === 0) return null;
    const toTime = value => value ? new Date(value).getTime() : 0;
    const sorted = [...rejectedSubmissions].sort((a, b) => {
      const aTime = toTime(a.reviewedAt) || toTime(a.updatedAt) || toTime(a.createdAt);
      const bTime = toTime(b.reviewedAt) || toTime(b.updatedAt) || toTime(b.createdAt);
      return bTime - aTime;
    });
    return sorted[0] ?? null;
  }, [rejectedSubmissions]);
  const activeSubmission = latestPendingSubmission || latestRejectedSubmission || null;
  const activeSubmissionId = activeSubmission?.id ?? null;
  const {
    data: rejectedTasks = []
  } = useGetSubmissionTasksQuery(activeSubmissionId && token ? activeSubmissionId : skipToken);
  const latestMessage = useMemo(() => {
    if (!rejectedTasks.length) return null;
    const sorted = [...rejectedTasks].sort((a, b) => {
      const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return bTime - aTime;
    });
    const latestEdit = sorted[0];
    const messages = Array.isArray(latestEdit?.messages) ? latestEdit.messages : [];
    if (!messages.length) return null;
    const lastMessage = messages[messages.length - 1];
    return lastMessage?.body || null;
  }, [rejectedTasks]);
  const latestAdminMessage = useMemo(() => {
    const adminMessages = rejectedTasks.flatMap(edit => (edit.messages || []).filter(msg => msg.authorRole === "admin"));
    if (!adminMessages.length) return null;
    return adminMessages.reduce((latest, msg) => {
      if (!latest) return msg;
      const latestTime = latest.createdAt ? new Date(latest.createdAt).getTime() : 0;
      const msgTime = msg.createdAt ? new Date(msg.createdAt).getTime() : 0;
      return msgTime >= latestTime ? msg : latest;
    }, null);
  }, [rejectedTasks]);
  const pendingCount = pendingSubmissions.length;
  const rejectedCount = rejectedSubmissions.length;
  const hasPendingForUser = pendingCount > 0 && isAuthenticated;
  const hasRejectedForUser = !hasPendingForUser && rejectedCount > 0 && isAuthenticated;
  const submissionStatusLabel = hasPendingForUser ? pendingCount === 1 ? "1 submission pending" : `${pendingCount} submissions pending` : rejectedCount === 1 ? "1 submission rejected" : `${rejectedCount} submissions rejected`;
  const [rejectedModalOpen, setRejectedModalOpen] = useState(false);
  const [discardSubmission] = useDiscardSubmissionMutation();

  // Saved organizations
  const {
    items: savedOrganizations,
    loading: savedOrganizationsLoading,
    initialized: savedOrganizationsInitialized,
    refresh: refreshSavedOrganizations
  } = useSavedItems("organization");
  const initialHasSavedOrganizations = savedOrganizations.length > 0;
  const [prioritizeSaved, setPrioritizeSaved] = useState(initialHasSavedOrganizations);
  const backendOrgsRef = useRef(null);
  const navigateWithAdminCheck = useCallback(target => {
    if (!isAuthenticated) {
      openAccessGateModal({
        reason: "login-required",
        returnUrl: target
      });
      return;
    }
    router.push(target);
  }, [isAuthenticated, router]);
  const lastSavedLookupRef = React.useRef(new Set());
  const savedLookup = React.useMemo(() => {
    const normalize = value => String(value ?? "").trim().toLowerCase();
    const addSlugVariants = (set, slug) => {
      const normalized = normalize(slug);
      if (!normalized) return;
      set.add(normalized);
      const trimmed = normalized.replace(/^\/+|\/+$/g, "");
      if (trimmed) {
        set.add(trimmed);
        const segments = trimmed.split("/").filter(Boolean);
        const last = segments[segments.length - 1];
        if (last) set.add(last);
      }
    };
    const set = new Set();
    savedOrganizations.forEach(item => {
      const resourceId = normalize(item.resourceId);
      if (resourceId) set.add(resourceId);
      addSlugVariants(set, item.resourceSlug);
      const title = normalize(item.resourceTitle);
      if (title) set.add(title);
      const meta = item.meta;
      if (meta && typeof meta === "object") {
        const metaRecord = meta;
        ["routeId", "organizationRouteId", "slug", "resourceSlug"].forEach(key => {
          const value = metaRecord[key];
          if (typeof value === "string") {
            addSlugVariants(set, value);
          }
        });
      }
    });
    if (set.size > 0 || !savedOrganizationsLoading) {
      lastSavedLookupRef.current = set;
      return set;
    }
    return lastSavedLookupRef.current;
  }, [savedOrganizations, savedOrganizationsLoading]);
  const hasSavedOrganizations = savedOrganizationsInitialized && savedLookup.size > 0;

  // Refresh saved organizations on page focus
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!router.isReady) return;
    if (!isAuthenticated) return;
    let cancelled = false;
    const ensureSavedOrgs = () => {
      if (cancelled) return;
      void refreshSavedOrganizations();
    };
    ensureSavedOrgs();
    const handleRouteChangeComplete = url => {
      if (url.startsWith("/organizations")) {
        ensureSavedOrgs();
      }
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        ensureSavedOrgs();
      }
    };
    const handleFocus = () => {
      ensureSavedOrgs();
    };
    router.events?.on("routeChangeComplete", handleRouteChangeComplete);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);
    return () => {
      cancelled = true;
      router.events?.off("routeChangeComplete", handleRouteChangeComplete);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, [router.isReady, router.events, isAuthenticated, refreshSavedOrganizations]);
  const handleOrganizationClick = useCallback(org => {
    // Navigate directly to the canonical id URL. Falls back to
    // slug only if the org has no backend id (legacy/local data).
    const target = `/organizations/${String(org.backendId || org.id || org.slug || "")}`;
    router.push(target);
  }, [router]);
  const handleCreateClick = useCallback(() => {
    if (hasPendingForUser || hasRejectedForUser) {
      setRejectedModalOpen(true);
      return;
    }
    navigateWithAdminCheck("/organizations/create");
  }, [hasPendingForUser, hasRejectedForUser, navigateWithAdminCheck]);
  const handleReviewRejected = useCallback(() => {
    if (!activeSubmission) return;
    setCreatePrefill({
      type: "organization",
      payload: activeSubmission.payload,
      reason: latestAdminMessage?.body || undefined,
      submissionId: activeSubmission.id
    });
    setRejectedModalOpen(false);
    navigateWithAdminCheck("/organizations/create");
  }, [activeSubmission, latestAdminMessage, navigateWithAdminCheck]);
  const handleCreateNew = useCallback(() => {
    clearCreatePrefill();
    setRejectedModalOpen(false);
    navigateWithAdminCheck("/organizations/create");
  }, [navigateWithAdminCheck]);
  const handleDiscardSubmission = useCallback(async () => {
    if (!activeSubmission) return;
    try {
      await discardSubmission(activeSubmission.id).unwrap();
      clearCreatePrefill();
      removeRejectedById("organization", activeSubmission.id);
      setRejectedModalOpen(false);
    } catch (error) {
      console.error("Failed to discard submission:", error);
    }
  }, [activeSubmission, discardSubmission, clearCreatePrefill, removeRejectedById]);
  const handleCreateCsvClick = useCallback(() => {
    navigateWithAdminCheck("/organizations/create?openImport=1");
  }, [navigateWithAdminCheck]);

  // Dedupe by route (slug) keeping most recent
  const dedupeByRoute = useCallback(arr => {
    const map = new Map();
    arr.forEach((org, idx) => {
      const routeId = buildOrganizationRouteId(org, {
        existing: arr
      }).toLowerCase();
      const key = `/organizations/${routeId}`;
      const currentTimestamp = getOrgFreshnessTimestamp(org);
      const previous = map.get(key);
      if (!previous) {
        map.set(key, {
          item: org,
          idx
        });
        return;
      }
      const previousTimestamp = getOrgFreshnessTimestamp(previous.item);
      if (currentTimestamp > previousTimestamp || currentTimestamp === previousTimestamp && idx > previous.idx) {
        map.set(key, {
          item: org,
          idx
        });
      }
    });
    return Array.from(map.values()).map(entry => entry.item);
  }, []);

  // Recompute from base + local approvals
  const recomputeOrganizations = React.useCallback(() => {
    // Fall through to the persisted index cache: the mount-time call can
    // fire before the cache-hydration effect's setState lands, and with
    // the query skipped (fresh cache) and no SSR seed it would otherwise
    // compute [] and clobber the cache-hydrated list.
    const baseSource = backendOrgsRef.current?.length ? backendOrgsRef.current : organizationsQuery?.length ? organizationsQuery : organizations?.length ? organizations : getIndexCache("organizations") ?? [];
    const baseWithAssets = baseSource.map(org => ({
      ...org,
      logoUrl: org.logoUrl ? assetUrl(org.logoUrl) : org.logoUrl,
      bannerImages: Array.isArray(org.bannerImages) ? org.bannerImages.map(img => assetUrl(img)) : org.bannerImages,
      __approvedTs: getOrgFreshnessTimestamp(org)
    }));
    const baseDeduped = dedupeByRoute(dedupeOrganizations(baseWithAssets.map(org => ({
      ...org
    }))));
    const baseMap = new Map();
    baseDeduped.forEach(org => {
      const key = getOrganizationKey(org);
      if (!key) return;
      const existing = baseMap.get(key);
      if (!existing) {
        baseMap.set(key, {
          ...org,
          __approvedTs: getOrgFreshnessTimestamp(org)
        });
        return;
      }
      const existingTs = getOrgFreshnessTimestamp(existing);
      const nextTs = getOrgFreshnessTimestamp(org);
      const preferNext = !!org.backendId && !existing.backendId || nextTs > existingTs;
      if (preferNext) {
        baseMap.set(key, {
          ...org,
          __approvedTs: nextTs
        });
      }
    });
    let newestApplied = null;
    const newly = getAllNewlyAdded("organization");
    if (newly.length) {
      const sorted = [...newly].sort((a, b) => a.approvalTimestamp - b.approvalTimestamp);
      sorted.forEach(item => {
        const payload = {
          ...item.payload,
          latestApprovalTimestamp: Math.max(item.payload.latestApprovalTimestamp ?? 0, item.approvalTimestamp),
          isEdit: item.isEdit ?? undefined
        };
        payload.logoUrl = payload.logoUrl ? assetUrl(payload.logoUrl) : payload.logoUrl;
        payload.bannerImages = Array.isArray(payload.bannerImages) ? payload.bannerImages.map(img => assetUrl(img)) : payload.bannerImages;
        const key = getOrganizationKey(payload);
        if (!key) return;
        const base = baseMap.get(key);
        if (!base) return;
        const overlayTs = Math.max(getOrgFreshnessTimestamp(payload), item.approvalTimestamp);
        const baseTs = getOrgFreshnessTimestamp(base);
        if (overlayTs <= baseTs) return;
        const merged = {
          ...base,
          ...payload,
          latestApprovalTimestamp: Math.max(base.latestApprovalTimestamp ?? 0, payload.latestApprovalTimestamp ?? 0, overlayTs),
          __approvedTs: Math.max(base.__approvedTs ?? baseTs, overlayTs)
        };
        baseMap.set(key, merged);
        if (!newestApplied || overlayTs > newestApplied.ts) {
          newestApplied = {
            ts: overlayTs,
            id: item.id
          };
        }
      });
      if (newestApplied) {
        const day = new Date(newestApplied.ts).toISOString().slice(0, 10);
        setDbVersion(`${day}:${newestApplied.id}`);
      }
    }
    if (newly.length) {
      trackMerge("organizations", newly.length);
    }
    const final = dedupeByRoute(Array.from(baseMap.values()));
    setOrgs(final);
    if (backendOrgsRef.current?.length) {
      setIndexCache("organizations", final);
    }
  }, [organizations, organizationsQuery, dedupeByRoute]);

  // Run on mount and whenever we return to this page
  useEffect(() => {
    // Check the persisted cache DIRECTLY: the cachedOrgs state is still
    // null on the first pass (its hydration setState hasn't landed), so
    // relying on it ran a full recompute over the whole list on every
    // cached visit for nothing — the hydration effect + recompute's own
    // cache fallback already cover the cold path.
    if (!cachedOrgs && !getIndexCache("organizations")?.length) {
      recomputeOrganizations();
    }
    const onNotif = () => recomputeOrganizations();
    const onStorage = e => {
      if (!e || !e.key || !("localStorage" in window)) return;
      if (e.key.startsWith("pc_newlyAdded_") || e.key === "pc_db_version") recomputeOrganizations();
    };
    const onFocus = () => {
      try {
        void refetchOrganizations();
      } catch {
        /* query not yet started */
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        try {
          void refetchOrganizations();
        } catch {
          /* query not yet started */
        }
      }
    };
    const onDeactivated = () => recomputeOrganizations();
    if (typeof window !== "undefined") {
      window.addEventListener("pc-notifications-changed", onNotif);
      window.addEventListener("storage", onStorage);
      if (!cachedOrgs) {
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onVisibility);
      }
      window.addEventListener("pc-deactivated-changed", onDeactivated);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("pc-notifications-changed", onNotif);
        window.removeEventListener("storage", onStorage);
        window.removeEventListener("focus", onFocus);
        document.removeEventListener("visibilitychange", onVisibility);
        window.removeEventListener("pc-deactivated-changed", onDeactivated);
      }
    };
  }, [recomputeOrganizations, refetchOrganizations]);

  // Sync backend RTK Query results into local cache and recompute view
  useEffect(() => {
    if (!organizationsQuery || !organizationsQuery.length) return;
    const withAssets = organizationsQuery.map(org => ({
      ...org,
      logoUrl: org.logoUrl ? assetUrl(org.logoUrl) : org.logoUrl,
      bannerImages: Array.isArray(org.bannerImages) ? org.bannerImages.map(img => assetUrl(img)) : org.bannerImages,
      __approvedTs: getOrgFreshnessTimestamp(org)
    }));
    dispatch(upsertListItems(withAssets));
    const deduped = dedupeByRoute(dedupeOrganizations(withAssets));
    backendOrgsRef.current = deduped;
    recomputeOrganizations();
  }, [organizationsQuery, dedupeByRoute, recomputeOrganizations, dispatch]);

  // Re-fetch from backend when navigating back or tab gains focus
  useEffect(() => {
    if (!router.isReady || cachedOrgs) return;
    const handleRouteChange = url => {
      if (url.startsWith("/organizations")) {
        try {
          void refetchOrganizations();
        } catch {
          // Query may not have started yet — ignore
        }
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        try {
          void refetchOrganizations();
        } catch {
          /* query not yet started */
        }
      }
    };
    const handleFocus = () => {
      try {
        void refetchOrganizations();
      } catch {
        /* query not yet started */
      }
    };
    router.events?.on("routeChangeComplete", handleRouteChange);
    if (typeof window !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibility);
      window.addEventListener("focus", handleFocus);
    }
    return () => {
      router.events?.off("routeChangeComplete", handleRouteChange);
      if (typeof window !== "undefined") {
        document.removeEventListener("visibilitychange", handleVisibility);
        window.removeEventListener("focus", handleFocus);
      }
    };
  }, [router, router.isReady, refetchOrganizations]);

  // ── Filter state ──────────────────────────────────────────────────────
  // `searchQuery` is the raw text shown in the input (persisted/restored).
  // `effectiveSearch` is the gated query (>= 3 chars) the SearchBar reports;
  // it drives the all-field in-memory filter below.
  const [searchQuery, setSearchQuery] = useState("");
  const [effectiveSearch, setEffectiveSearch] = useState("");
  const [viewMode, setViewMode] = useState("list");
  const [filterRegion, setFilterRegion] = useState("");
  const [filterDiscipline, setFilterDiscipline] = useState("");
  const [filterProduct, setFilterProduct] = useState("");

  // Dropdown options derived from filterTagData.json
  const regionOptions = useMemo(() => filterTagData.regions.map(r => r.name), []);
  const disciplineOptions = useMemo(() => filterTagData.disciplines.filter(d => typeof d.order === "number").sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(d => d.name), []);
  const productOptions = useMemo(() => filterTagData.products.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map(p => p.name), []);

  // Restore persisted filter state on mount; clear only if nothing was saved
  useEffect(() => {
    const saved = getSavedFilters();
    if (saved.searchQuery !== undefined) {
      setSearchQuery(saved.searchQuery || "");
    } else {
      setSearchQuery("");
    }
    if (saved.discipline !== undefined) {
      setFilterDiscipline(saved.discipline || "");
    } else {
      setFilterDiscipline("");
    }
    if (saved.product !== undefined) {
      setFilterProduct(saved.product || "");
    } else {
      setFilterProduct("");
    }
    if (saved.region !== undefined) {
      setFilterRegion(saved.region || "");
    }
    // regions (chips) are handled by the existing saved-filter mechanism
  }, []);

  // Guard: skip the very first persist run (which fires before the restore
  // effect's setState calls have propagated). Without this, the persist effect
  // would overwrite the saved values with empty initial state, clearing the
  // filters that should be restored on back-navigation.
  const filterPersistAllowedRef = useRef(false);

  // Persist filter changes to localStorage whenever they change (debounced by state update cycle)
  useEffect(() => {
    if (!filterPersistAllowedRef.current) {
      // First run — state is still at "" initial values; allow subsequent runs.
      filterPersistAllowedRef.current = true;
      return;
    }
    const saved = getSavedFilters();
    setSavedFilters({
      ...saved,
      searchQuery,
      discipline: filterDiscipline,
      product: filterProduct,
      region: filterRegion
    });
  }, [searchQuery, filterDiscipline, filterProduct, filterRegion]);

  // Bootstrap filter state from query params
  useEffect(() => {
    if (!router.isReady) return;
    const qDisc = router.query.discipline;
    if (typeof qDisc === "string" && qDisc.trim()) {
      setFilterDiscipline(qDisc);
    }
    const qProd = router.query.product;
    if (typeof qProd === "string" && qProd.trim()) {
      setFilterProduct(qProd);
    }
  }, [router.isReady, router.query.discipline, router.query.product]);
  const prioritizeSavedTouchedRef = useRef(false);
  useEffect(() => {
    if (!savedOrganizationsInitialized) {
      return;
    }
    if (!hasSavedOrganizations) {
      prioritizeSavedTouchedRef.current = false;
      setPrioritizeSaved(false);
      return;
    }
    if (!prioritizeSavedTouchedRef.current) {
      setPrioritizeSaved(true);
    }
  }, [hasSavedOrganizations, savedOrganizationsInitialized]);

  // Compute display list
  const displayOrgs = React.useMemo(() => {
    // Skip the deactivation filter on the very first client render so the
    // DOM matches the SSR output (localStorage is unavailable on the
    // server, so isOrgDeactivated always returns false there). Once
    // mounted=true, a re-render applies the real filter.
    const base = mounted ? orgs.filter(o => {
      const key = String(o.slug || o.id || "");
      return !isOrgDeactivated(key);
    }) : orgs;

    // All-field instant search via the shared site-search engine: matches
    // name, description, focus areas, location (city/state/country), etc.,
    // not just the org name. Runs entirely over the already-loaded `orgs`
    // (no network request). `effectiveSearch` is gated to >= minChars by the
    // SearchBar, so 1–2 characters don't filter the list.
    const term = effectiveSearch.trim();
    let searchedBase = base;
    if (term) {
      const sections = searchSite(term, {
        organizations: base
      }, {
        eventsVisible: false,
        regulationsVisible: false,
        articlesVisible: false
      });
      const orgSection = sections.find(s => s.type === "organization");
      const matchedIds = new Set((orgSection?.results ?? []).map(r => r.id));
      searchedBase = base.filter(o => matchedIds.has(String(o.id || o.slug)));
    }
    const filtered = searchedBase.filter(o => {
      if (filterRegion) {
        const regs = o.regionalFocus || [];
        const matches = regs.some(r => doesRegionLabelMatchSelection(r, filterRegion));
        if (!matches) return false;
      }
      if (filterDiscipline) {
        const discs = o.orgFocus || [];
        const dl = filterDiscipline.toLowerCase();
        const matches = discs.some(d => String(d || "").toLowerCase() === dl);
        if (!matches) return false;
      }
      if (filterProduct) {
        const prods = o.productFocus || [];
        const pl = filterProduct.toLowerCase();
        const matches = prods.some(p => String(p || "").toLowerCase() === pl);
        if (!matches) return false;
      }
      return true;
    });
    const sorted = [...filtered].sort((a, b) => String(a.orgName || "").localeCompare(String(b.orgName || "")));
    if (prioritizeSaved && savedLookup.size) {
      const isSavedOrg = item => {
        const slug = String(item.slug || "").trim().toLowerCase();
        const id = String(item.id || "").trim().toLowerCase();
        const backendId = String(item.backendId || "").trim().toLowerCase();
        return slug && savedLookup.has(slug) || id && savedLookup.has(id) || backendId && savedLookup.has(backendId);
      };
      const savedItems = [];
      const regularItems = [];
      sorted.forEach(item => {
        if (isSavedOrg(item)) savedItems.push(item);else regularItems.push(item);
      });
      return [...savedItems, ...regularItems];
    }
    return sorted;
  }, [orgs, effectiveSearch, filterRegion, filterDiscipline, filterProduct, prioritizeSaved, savedLookup, mounted]);

  // Progressive loading
  const {
    visibleItems: visibleOrgs,
    showSkeletons: showOrgSkeletons
  } = useProgressiveLoading(displayOrgs);

  // Relevance split based on tag preferences
  const {
    prefs: tagPrefs
  } = useTagPreferences();
  // Gate tagPrefs behind mounted so the first client render matches the
  // server (localStorage is unavailable during SSR, so prefs are always
  // empty there). Without this guard React throws a hydration error when
  // a user has saved preferences: server renders the flat Grid while the
  // client immediately renders the "Most Relevant" Box wrapper.
  const effectiveTagPrefs = mounted ? tagPrefs : {
    disciplines: [],
    products: [],
    regions: []
  };
  const {
    relevantOrgs,
    remainingOrgs,
    showRelevanceSection
  } = React.useMemo(() => {
    const hasPrefs = effectiveTagPrefs.disciplines.length > 0 || effectiveTagPrefs.products.length > 0 || effectiveTagPrefs.regions.length > 0;
    if (!hasPrefs || visibleOrgs.length === 0) {
      return {
        relevantOrgs: visibleOrgs,
        remainingOrgs: [],
        showRelevanceSection: false
      };
    }
    const discLowerPrefs = new Set(effectiveTagPrefs.disciplines.map(d => d.toLowerCase()));
    const prodLowerPrefs = new Set(effectiveTagPrefs.products.map(p => p.toLowerCase()));
    const regionLowerPrefs = new Set(effectiveTagPrefs.regions.map(r => r.toLowerCase()));
    const scored = visibleOrgs.map((org, idx) => {
      let score = 0;
      (org.orgFocus || []).forEach(d => {
        if (discLowerPrefs.has(String(d || "").toLowerCase())) score += 1;
      });
      (org.productFocus || []).forEach(p => {
        if (prodLowerPrefs.has(String(p || "").toLowerCase())) score += 1;
      });
      (org.regionalFocus || []).forEach(r => {
        if (regionLowerPrefs.has(String(r || "").toLowerCase()) || doesRegionMatchPrefs(r, effectiveTagPrefs.regions)) score += 1;
      });
      return {
        org,
        score,
        idx
      };
    });
    const relevant = scored.filter(s => s.score > 0).sort((a, b) => b.score - a.score || a.idx - b.idx).map(s => s.org);
    const remaining = scored.filter(s => s.score === 0).map(s => s.org);

    // When prioritize-saved is on, float saved orgs to the top
    // within each section while preserving relative order.
    const pushSavedToFront = list => {
      if (!prioritizeSaved || !savedLookup.size) return list;
      const saved = [];
      const rest = [];
      list.forEach(item => {
        const slug = String(item.slug || "").trim().toLowerCase();
        const id = String(item.id || "").trim().toLowerCase();
        const backendId = String(item.backendId || "").trim().toLowerCase();
        const isSaved = slug && savedLookup.has(slug) || id && savedLookup.has(id) || backendId && savedLookup.has(backendId);
        if (isSaved) saved.push(item);else rest.push(item);
      });
      return [...saved, ...rest];
    };
    return {
      relevantOrgs: pushSavedToFront(relevant),
      remainingOrgs: pushSavedToFront(remaining),
      showRelevanceSection: relevant.length > 0 && remaining.length > 0
    };
  }, [visibleOrgs, effectiveTagPrefs, prioritizeSaved, savedLookup]);
  const hasOrgData = Boolean(orgs.length) || Boolean(organizationsQuery && organizationsQuery.length) || Boolean(organizations && organizations.length);
  const showLoadingSkeleton = !hasOrgData && savedOrganizationsLoading && !savedOrganizationsInitialized;

  // ── Filter sets for FilterBar ──
  const regionFilterSet = useMemo(() => ({
    name: "Region",
    options: regionOptions,
    value: filterRegion,
    onChange: setFilterRegion
  }), [regionOptions, filterRegion]);
  const disciplineFilterSet = useMemo(() => ({
    name: "Discipline",
    options: disciplineOptions,
    value: filterDiscipline,
    onChange: setFilterDiscipline
  }), [disciplineOptions, filterDiscipline]);
  const productFilterSet = useMemo(() => ({
    name: "Product",
    options: productOptions,
    value: filterProduct,
    onChange: setFilterProduct
  }), [productOptions, filterProduct]);

  // ── Empty state ──
  // Checks EVERY source that can feed cards (client state, query, SSR
  // prop) — the SSR prop alone is always empty post-static-export, which
  // used to append this block under a fully populated grid.
  const emptyState = !hasOrgData ? /*#__PURE__*/_jsxs(Box, {
    sx: {
      textAlign: "center",
      py: 8
    },
    children: [/*#__PURE__*/_jsx(Typography, {
      variant: "h5",
      color: "text.secondary",
      gutterBottom: true,
      children: "No organizations found"
    }), /*#__PURE__*/_jsx(Typography, {
      variant: "body1",
      color: "text.secondary",
      paragraph: true,
      children: "Be the first to add your organization to our directory."
    }), /*#__PURE__*/_jsx(Box, {
      sx: {
        display: "flex",
        justifyContent: "center",
        width: "100%"
      },
      children: /*#__PURE__*/_jsxs(Box, {
        sx: {
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 0.5,
          width: "fit-content"
        },
        children: [/*#__PURE__*/_jsxs(Box, {
          sx: {
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            maxWidth: "100%"
          },
          children: [/*#__PURE__*/_jsx(Badge, {
            color: hasPendingForUser ? "info" : "error",
            badgeContent: hasPendingForUser ? pendingCount : rejectedCount,
            invisible: !(hasPendingForUser || hasRejectedForUser),
            children: /*#__PURE__*/_jsx(Button, {
              variant: "contained",
              onClick: handleCreateClick,
              sx: {
                bgcolor: orange.primary,
                "&:hover": {
                  bgcolor: orange.dark
                }
              },
              children: "Add Your Organization"
            })
          }), isAdmin && /*#__PURE__*/_jsx(Button, {
            variant: "outlined",
            onClick: handleCreateCsvClick,
            sx: {
              display: {
                xs: "none",
                md: "inline-flex"
              },
              borderColor: orange.primary,
              color: orange.primary,
              "&:hover": {
                borderColor: orange.dark,
                color: orange.dark,
                bgcolor: "transparent"
              }
            },
            children: "Add by CSV"
          })]
        }), hasPendingForUser || hasRejectedForUser ? /*#__PURE__*/_jsx(Button, {
          variant: "text",
          color: hasPendingForUser ? "info" : "error",
          size: "small",
          onClick: () => setRejectedModalOpen(true),
          sx: {
            textTransform: "none",
            fontWeight: 600,
            alignSelf: "flex-start"
          },
          children: submissionStatusLabel
        }) : null]
      })
    })]
  }) : null;
  const indexLayout = useIndexLayout("organizations");
  // Same query the featured section subscribes to — RTK serves it from
  // one cache entry, so this costs no extra request. Style 3 needs to
  // know: a live banner REPLACES the hero, mirroring the events page.
  const {
    data: featuredOrgPromos
  } = useGetPromotionsQuery("featured_organization");
  const hasFeaturedOrgBanner = indexLayout.showFeatured && (featuredOrgPromos?.length ?? 0) > 0;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(Head, {
      children: [/*#__PURE__*/_jsx("title", {
        children: "Organizations Directory | Your Company"
      }), /*#__PURE__*/_jsx("meta", {
        name: "description",
        content: "Discover organizations in our directory. Find consulting firms, certification services, and industry leaders to help with your business needs."
      }), /*#__PURE__*/_jsx("meta", {
        name: "keywords",
        content: "organizations, directory, consulting, certification, business services, compliance"
      }), /*#__PURE__*/_jsx("meta", {
        property: "og:title",
        content: "Organizations Directory | Your Company"
      }), /*#__PURE__*/_jsx("meta", {
        property: "og:description",
        content: "Discover organizations in our directory. Find consulting firms and certification services."
      }), /*#__PURE__*/_jsx("meta", {
        property: "og:type",
        content: "website"
      }), /*#__PURE__*/_jsx("meta", {
        property: "og:url",
        content: "https://yourdomain.com/organizations"
      }), /*#__PURE__*/_jsx("meta", {
        name: "twitter:card",
        content: "summary"
      }), /*#__PURE__*/_jsx("meta", {
        name: "twitter:title",
        content: "Organizations Directory | Your Company"
      }), /*#__PURE__*/_jsx("meta", {
        name: "twitter:description",
        content: "Discover organizations in our directory. Find consulting firms and certification services."
      }), /*#__PURE__*/_jsx("link", {
        rel: "canonical",
        href: "https://yourdomain.com/organizations"
      }), /*#__PURE__*/_jsx("script", {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Organizations Directory",
          description: "Discover organizations in our directory. Find consulting firms and certification services.",
          url: "https://yourdomain.com/organizations"
        })
      })]
    }), /*#__PURE__*/_jsx(MainContainer, {
      sx: panelPageSx(indexLayout.style, orange.primary),
      children: /*#__PURE__*/_jsxs(ContentWrapper, {
        children: [/*#__PURE__*/_jsx(NavBar, {}), indexLayout.style === 3 && /*#__PURE__*/_jsxs(_Fragment, {
          children: [indexLayout.showFeatured && /*#__PURE__*/_jsx(FeaturedOrganizationSection, {
            orgs: orgs,
            onVisit: o => handleOrganizationClick(o),
            loading: showLoadingSkeleton
          }), !hasFeaturedOrgBanner && /*#__PURE__*/_jsx(OrganizationDirectoryHeader, {
            style: 3,
            showCreate: true,
            showUpload: indexLayout.showUpload,
            hasPendingForUser: hasPendingForUser,
            hasRejectedForUser: hasRejectedForUser,
            pendingCount: pendingCount,
            rejectedCount: rejectedCount,
            submissionStatusLabel: submissionStatusLabel,
            isAdmin: isAdmin,
            onCreateClick: handleCreateClick,
            onCreateCsvClick: handleCreateCsvClick,
            onSubmissionStatusClick: () => setRejectedModalOpen(true)
          })]
        }), /*#__PURE__*/_jsxs(StyledContainer, {
          maxWidth: "lg",
          component: "main",
          id: MAIN_CONTENT_ID,
          tabIndex: -1,
          children: [indexLayout.style === 3 && hasFeaturedOrgBanner &&
          /*#__PURE__*/
          // Banner took the hero's place — surface the
          // title and actions above the filter bar in the
          // directory treatment, as events does.
          _jsx(OrganizationDirectoryHeader, {
            style: 1,
            showCreate: true,
            showUpload: indexLayout.showUpload,
            hasPendingForUser: hasPendingForUser,
            hasRejectedForUser: hasRejectedForUser,
            pendingCount: pendingCount,
            rejectedCount: rejectedCount,
            submissionStatusLabel: submissionStatusLabel,
            isAdmin: isAdmin,
            onCreateClick: handleCreateClick,
            onCreateCsvClick: handleCreateCsvClick,
            onSubmissionStatusClick: () => setRejectedModalOpen(true)
          }), indexLayout.style === 2 && indexLayout.showFeatured && /*#__PURE__*/_jsx(FeaturedOrganizationSection, {
            orgs: orgs,
            onVisit: o => handleOrganizationClick(o),
            loading: showLoadingSkeleton
          }), /*#__PURE__*/_jsx(IndexPanelSurface, {
            style: indexLayout.style,
            children: indexLayout.style !== 3 && /*#__PURE__*/_jsx(OrganizationDirectoryHeader, {
              style: indexLayout.style,
              showCreate: true,
              showUpload: indexLayout.showUpload,
              hasPendingForUser: hasPendingForUser,
              hasRejectedForUser: hasRejectedForUser,
              pendingCount: pendingCount,
              rejectedCount: rejectedCount,
              submissionStatusLabel: submissionStatusLabel,
              isAdmin: isAdmin,
              onCreateClick: handleCreateClick,
              onCreateCsvClick: handleCreateCsvClick,
              onSubmissionStatusClick: () => setRejectedModalOpen(true)
            })
          }), indexLayout.style === 1 && indexLayout.showFeatured && /*#__PURE__*/_jsx(FeaturedOrganizationSection, {
            orgs: orgs,
            onVisit: o => handleOrganizationClick(o),
            loading: showLoadingSkeleton
          }), /*#__PURE__*/_jsx(FilterBar, {
            selectTarget: "Organization Filters",
            search: true,
            searchPlaceholder: "Search organizations...",
            searchValue: searchQuery,
            onSearchChange: setSearchQuery,
            searchScope: "organizations",
            searchMinChars: 3,
            onSearch: q => setEffectiveSearch(q),
            filter1: regionFilterSet,
            filter2: disciplineFilterSet,
            filter3: productFilterSet,
            showPrioritizeSaved: hasSavedOrganizations,
            prioritizeSaved: prioritizeSaved,
            onPrioritizeSavedChange: checked => {
              prioritizeSavedTouchedRef.current = true;
              setPrioritizeSaved(checked);
            },
            viewMode: viewMode,
            onViewModeChange: setViewMode
          }), /*#__PURE__*/_jsx(ResultsSection, {
            mostRelevant: relevantOrgs,
            remainingResults: remainingOrgs,
            showRelevanceSection: showRelevanceSection,
            allOrgs: orgs,
            organizations: organizations,
            filterRegion: filterRegion,
            filterDiscipline: filterDiscipline,
            filterProduct: filterProduct,
            tagPrefs: effectiveTagPrefs,
            onOrganizationClick: handleOrganizationClick,
            loading: showLoadingSkeleton || showOrgSkeletons,
            emptyState: emptyState,
            viewMode: viewMode
          })]
        }), /*#__PURE__*/_jsxs(Dialog, {
          open: rejectedModalOpen,
          onClose: () => setRejectedModalOpen(false),
          maxWidth: "sm",
          fullWidth: true,
          children: [/*#__PURE__*/_jsx(DialogTitle, {
            children: hasPendingForUser ? "Submission pending review" : "Review current submission?"
          }), /*#__PURE__*/_jsxs(DialogContent, {
            dividers: true,
            children: [/*#__PURE__*/_jsx(Typography, {
              variant: "body2",
              color: "text.secondary",
              children: hasPendingForUser ? "Your submission is currently pending review. You can review the submission or start a new one if needed." : "We found a rejected organization submission tied to your account. You can review the feedback and resubmit, or start a brand-new submission."
            }), hasPendingForUser ? latestMessage ? /*#__PURE__*/_jsxs(Alert, {
              severity: "info",
              sx: {
                mt: 2
              },
              children: [/*#__PURE__*/_jsx("strong", {
                children: "Pending message:"
              }), " ", latestMessage]
            }) : /*#__PURE__*/_jsx(Alert, {
              severity: "info",
              sx: {
                mt: 2
              },
              children: "No messages yet. Your submission is queued for review."
            }) : latestAdminMessage?.body ? /*#__PURE__*/_jsxs(Alert, {
              severity: "error",
              sx: {
                mt: 2
              },
              children: [/*#__PURE__*/_jsx("strong", {
                children: "Rejection message:"
              }), " ", latestAdminMessage.body]
            }) : /*#__PURE__*/_jsx(Alert, {
              severity: "info",
              sx: {
                mt: 2
              },
              children: "No rejection message was provided. You can still review and resubmit your submission."
            })]
          }), /*#__PURE__*/_jsxs(DialogActions, {
            children: [/*#__PURE__*/_jsx(Button, {
              onClick: () => setRejectedModalOpen(false),
              children: "Cancel"
            }), hasRejectedForUser && /*#__PURE__*/_jsx(Button, {
              variant: "outlined",
              color: "error",
              onClick: handleDiscardSubmission,
              disabled: !activeSubmission,
              children: "Discard submission"
            }), /*#__PURE__*/_jsx(Button, {
              variant: "outlined",
              onClick: handleCreateNew,
              children: "Create new"
            }), /*#__PURE__*/_jsx(Button, {
              variant: "contained",
              onClick: handleReviewRejected,
              disabled: !activeSubmission,
              children: "Review current submission"
            })]
          })]
        }), /*#__PURE__*/_jsx(SectionFooter, {})]
      })
    })]
  });
};
export default OrganizationIndexPage;